import {MapConfig, MapLayerConfig} from './MapConfig';
import {Feature, Map, Overlay, View} from 'ol';
import {fromLonLat, transformExtent} from 'ol/proj';
import LayerGroup from 'ol/layer/Group';
import {Bing} from './Plugins/Layers/Bing';
import {Geojson, GeojsonLayerConfig} from './Plugins/Layers/Geojson';
import {OSM} from './Plugins/Layers/OSM';
import {createEmpty, extend, Extent, isEmpty} from 'ol/extent';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import {FeatureLike} from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import {Cluster} from "ol/source";
import Colorize from "ol-ext/filter/Colorize";
import {fromExtent} from "ol/geom/Polygon";
import {WKT} from "ol/format";
import {Tilewms} from "./Plugins/Layers/Tilewms";


export function ajaxUpdateMap(openlayers: Openlayers) {
  if (openlayers.initializing) {
    return;
  }

  if (openlayers.map.get('drawing')) {
    return;
  }

  if (openlayers.isAjaxUpdating) {
    return;
  }

  if(!openlayers.mapConfig.getView().ajax_on_view_change){
    return;
  }


  let view = openlayers.map.getView();

  let extent = view.calculateExtent(openlayers.map.getSize());
  extent = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');


  const element_settings = openlayers.viewsInstance['element_settings'];
  const url = new URL(openlayers.destination ?? element_settings.url, window.location.origin);

  const settings = openlayers.viewsInstance['settings'];
  const view_args = (new WKT().writeGeometry(fromExtent(extent)));

  // Allow other places to alter views args.
  let event = new CustomEvent('views_args_event', {
    detail: {
      views_args: view_args
    }
  })
  openlayers.element.dispatchEvent(event)
  settings.view_args = event.detail.views_args;
  settings._exposed_input = url.toString();

  // Set the _on_moovend key so we know how the map+list
  // should be updated. THis has to be removed manually
  // from the ajax instances later one.
  settings._on_moovend = '1';

  // Indicate that this originates from a map change, so the zoom effect doesn't trigger
  // an update of the map itself.
  element_settings.base = false;
  element_settings.event = 'map_zoom';
  element_settings.url = url.toString();
  url.searchParams.set('page', '0');
  element_settings.submit = settings
  element_settings.progress.type = 'openlayers';
  openlayers.isAjaxUpdating = true;
  let ajax = Drupal.ajax(element_settings);
  ajax.execute().done((commands, statusString, ajaxObject) => {

    openlayers.isAjaxUpdating = false;

    Object.entries(Drupal.ajax.instances).forEach(([key, instance]) => {
      if (instance?.submit?._on_moovend !== undefined) {
        Drupal.ajax.instances[key].submit._on_moovend = '0';
        delete Drupal.ajax.instances[key].submit._exposed_input;
        if (Drupal.ajax.instances[key].progress) {
          Drupal.ajax.instances[key].progress.type = 'openlayers';
        }
      }
    })
    Object.entries(Drupal.views.instances).forEach(([key, instance]) => {
      const settings = instance.settings;
      settings._on_moovend = '0';
      delete settings._exposed_input;
      Drupal.views.instances[key].settings = settings;
      if (Drupal.views.instances[key].progress) {
        Drupal.views.instances[key].progress.type = 'openlayers';
      }
    })

  });


}

/**
 * Class that instantiates the Openlayers maps.
 */
export class Openlayers {

  public element: HTMLElement;

  protected mapId: string;

  public mapConfig: MapConfig;

  public initializing: boolean;

  protected data: never;

  protected lazyData: never;

  public map: Map;

  protected tooltipElement: HTMLElement;

  public lazyLoadingProgressElement: HTMLElement;

  protected featureSelectContainer: HTMLElement;

  public sources: any;

  public clusterSource?: Cluster;

  public layers: any;

  public promisesCollection: PromiseLike<any>[];

  public layerPlugins: any = {};

  public destination: null | string = null;


  public isAjaxUpdating: boolean = false;

  public tools = {
    geojson: new GeoJSON(),
    getCombinedExtent: this.getCombinedExtent,
  };

  public viewsInstance;

  public geometryHashes: [] = [];

  /**
   * The Openlayers object constructor.
   *
   * @param map_id
   * @param map_config
   * @param data
   * @param lazy_data
   * @param tooltip_element
   * @param lazy_loading_progress_element
   * @param feature_select_container
   */
  constructor(
    element: HTMLElement,
    map_id: string,
    map_config: never,
    data: never,
    lazy_data: never,
    tooltip_element: HTMLElement,
    lazy_loading_progress_element: HTMLElement,
    feature_select_container: HTMLElement,
  ) {
    this.element = element;
    this.initializing = true;
    this.mapId = map_id;
    this.mapConfig = new MapConfig(map_config);
    this.tooltipElement = tooltip_element;
    this.lazyLoadingProgressElement = lazy_loading_progress_element;
    this.featureSelectContainer = feature_select_container;
    this.data = data;
    this.lazyData = lazy_data;
    // Initiate a collection of promises for all layers.
    this.promisesCollection = [];
    this.layers = [];
    this.sources = [];

    this.viewsInstance = Drupal.views.instances?.['views_dom_id:' + map_id.replace('js-view-openlayers-', '')];


    console.log('Creating map...');
    this.map = new Map({
      controls: [],
      target: map_id,
      view: new View({
        center: fromLonLat([
          this.mapConfig.getView().center.longitude,
          this.mapConfig.getView().center.latitude,
        ]),
        zoom: this.mapConfig.getView()['zoom'],
      }),
      overlays: [
        new Overlay({
          id: 'tooltip',
          element: tooltip_element,
          stopEvent: false,
        }),
      ],
    });


    this.map.getView().on('change:resolution', (event) => {
      this.geometryHashes = [];
    });


    this.initLayers();

    this.addClickInteractions();

    this.addHoverInteractions();

    this.addZoomInteractions();

    this.map.on('moveend', evt => {
      this.triggerChange();
    });


    // Zoom to extends.
    const extent = this.getCombinedExtent();

    if (!isEmpty(extent)) {
      this.map.getView().fit(extent, {
        padding: [100, 100, 100, 100],
        maxZoom: 15,
        callback: () => {
          // make sure it doesn't rerender on instantiating the map.
          setTimeout(() => {
            this.initializing = false;
          }, 500);
        }
      });
    }
  }

  /**
   * Get the combined extent of the map and all its sources.
   */
  getCombinedExtent(): Extent {
    const extent = createEmpty();
    this.map.getLayers().forEach((layer: BaseLayer) => {
      const layerConfig: GeojsonLayerConfig = this.mapConfig.getLayerConfig(layer.get('id'));
      if (layerConfig.fit_extents) {
        if (layer instanceof LayerGroup) {
          layer.getLayers().forEach((layer: BaseLayer) => {
            if (layer instanceof VectorLayer) {
              layer
                .getSource()
                .getFeatures()
                .forEach((feature: Feature) => {
                  if (!feature.get('hidden')) {
                    extend(extent, feature.getGeometry().getExtent());
                  }
                });
            }
          });
        } else if (layer instanceof VectorLayer) {
          layer
            .getSource()
            .getFeatures()
            .forEach((feature: Feature) => {
              if (!feature.get('hidden')) {
                extend(extent, feature.getGeometry().getExtent());
              }
            });
        }
      }
    });
    return extent;
  }

  /**
   * Build all the layers.
   */
  protected initLayers(): void {
    const layerConfigs: MapLayerConfig[] = this.mapConfig.getLayers();
    const layer_ids = Object.keys(layerConfigs);

    // Sort layers by weight.
    layer_ids.sort(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // @todo Fix typing.
      (a, b) => {
        return layerConfigs[a].weight - layerConfigs[b].weight;
      },
    );

    // Add layers to the map.
    // @todo Find way to work with plugins and variable class names.
    layer_ids.forEach((layer_id: string, index) => {
      const layerConfig: MapLayerConfig = layerConfigs[layer_id];
      const plugin_id = layerConfig.id;
      let layer;

      switch (plugin_id) {
        case 'geojson':
          layer = new Geojson(this, layer_id, layerConfig.weight, this);
          break;
        case 'bing':
          layer = new Bing(this, layer_id, layerConfig.weight);
          break;
        case 'osm':
          layer = new OSM(this, layer_id, layerConfig.weight);
          break;
        case'tilewms':
          layer = new Tilewms(this, layer_id, layerConfig.weight);
          break;
      }
      if (layer) {
        this.layerPlugins[layer_id] = layer;
        const build = layer.buildLayer();

        if (build) {
          build.setVisible(layerConfig.visibility);
          build.set('id', layer_id);
          this.map.addLayer(build);
          this.layers[layer_id] = build;
        }
      }
    });


    // Create a colorize instance so ol-ext adds
    // the addFilter methods.
    const filter = new Colorize({})

  }

  /**
   * Get the lazy data per layer.
   *
   * @param layer_id
   */
  public getLazyDataPerLayer(layer_id: string) {
    return this.lazyData[layer_id] ?? undefined;
  }

  /**
   * Get all the layers
   */
  public getLayers() {
    return this.layers;
  }

  /**
   * Get all the data keyed by layer id.
   */
  public getData() {
    return this.data;
  }

  /**
   * Get all the sources keyed by layer id.
   */
  public getSources() {
    return this.sources;
  }

  /**
   * Get all the data for a specific layer id.
   * @param layer_id
   */
  public getDataPerLayer(layer_id: string) {
    return this.data[layer_id];
  }

  /**
   * Gets the map id.
   */
  public getMapId() {
    return this.mapId;
  }


  /**
   * Get a source for a specific layer id.
   */
  public getSourceByLayerId(layer_id: string) {
    return this.sources[layer_id];
  }

  /**
   * Get the Openlayers Map object.
   */
  public getMap() {
    return this.map;
  }

  /**
   * Get all features in the map.
   */
  protected getAllFeatures = (): Feature[] => {
    const features: Feature[] = [];

    // Gather all features in all sources.
    for (const id in this.sources) {
      if (this.mapConfig.getLayerConfig(id).hover.property) {
        this.sources[id].getFeatures().forEach((feature: Feature) => {
          features.push(feature);
        });
      }
    }
    return features;
  };

  protected triggerChange = () => {

    this.geometryHashes = [];
    for (const id in this.sources) {
      if (this.mapConfig.getLayerConfig(id).hover.property) {
        this.sources[id].dispatchEvent('change');
      }
    }

  }


  /**
   * Explode features that might be inside a cluster feature.
   */
  public explodeClusterFeatures(features: Feature[]): Feature[] {
    for (const delta in features) {
      if (features[delta].get('features') != undefined) {
        const clusterFeatures = features[delta].get('features');
        features.splice(delta, 1);
        for (const clusterDelta in clusterFeatures) {
          features.push(clusterFeatures[clusterDelta]);
        }
      }
    }
    return features;
  }


  emptySelectContainer = () => {
    // Reset the tooltip container.
    if (this.map.get('feature_select_container')) {
      this.featureSelectContainer.classList.remove('active');
        document.body.classList.remove('feature-select-container-active');
      this.featureSelectContainer.innerHTML = '';
      this.map.set('feature_select_container', false);
      return;
    }
  }

  fillSelectContainer = (features: FeatureLike[]) => {

    const selectContainerElement = document.createElement('div');

    selectContainerElement.classList.add('inner');
    let uniqueFeatures = [];


    Object.values(features).forEach((feature: Feature) => {
      const tooltip = this.getTooltip(feature, true);
      if (tooltip) {
        if(!uniqueFeatures.includes(tooltip.innerHTML)) {
          tooltip.setAttribute('data-feature', feature.getId());
          selectContainerElement.appendChild(tooltip);
          uniqueFeatures.push(tooltip.innerHTML);
        }
      }
    })

    this.featureSelectContainer.appendChild(selectContainerElement);
    this.featureSelectContainer.classList.add('active');
    document.body.classList.add('feature-select-container-active');
    this.map.set('feature_select_container', true);

  }


  /**
   * Add click interactions to the map.
   */
  addClickInteractions(): void {
    this.map.on('click', (e) => {
      if (this.map.get('drawing')) {
        return;
      }

      // Only continue if features were clicked.
      const clickedFeatures = this.map.getFeaturesAtPixel(e.pixel);


      if (clickedFeatures.length === 0) {
        this.emptySelectContainer();
        return;
      }

      this.explodeClusterFeatures(clickedFeatures);

      for (const delta in clickedFeatures) {
        const feature = clickedFeatures[delta];
        if (!feature.get('_ajax_callback_url')) {
          clickedFeatures.splice(delta, 1);
        }
      }

      if (clickedFeatures.length === 1) {
        const currentFeature = clickedFeatures[0];
        this.executeAjaxCallback(currentFeature);
      } else if (clickedFeatures.length > 1 && this.featureSelectContainer) {
        this.emptySelectContainer();
        this.fillSelectContainer(clickedFeatures);

        this.tooltipElement.innerHTML = '';
        const tooltipLocator = document.createElement('div');
        tooltipLocator.classList.add('tooltip-locator');
        const coordinate = e.coordinate;
        this.map.getOverlayById('tooltip').setPosition(coordinate);
        this.tooltipElement.appendChild(tooltipLocator);
      }
    });
  }

  /**
   * Execute the Ajax callback.
   */
  executeAjaxCallback(currentFeature: FeatureLike): void {

    if (currentFeature.get('features')?.length == 1) {
      currentFeature = currentFeature.get('features')[0];
    }
    if (currentFeature.get('features')?.length > 1) {
      return;
    }
    if (!currentFeature.get('_ajax_callback_url')) {
      return;
    }

    // Add throbber. The throbber will be removed after finishing the request.
    const body = document.querySelector('body');
    if (!body) {
      throw 'No body?';
    }

    body.insertAdjacentHTML('afterEnd', Drupal.theme.ajaxProgressIndicatorFullscreen());

    // @todo Find out how we can decouple the Drupal ajax call from this Openlayers class.
    Drupal.ajax({url: currentFeature.get('_ajax_callback_url')})
      .execute()
      .done(function (commands, statusString, ajaxObject) {
        console.log('click callback executed.');
      });
  }

  /**
   * Create a tooltip.
   */
  getTooltip(currentFeature: FeatureLike, has_listeners: boolean = false): Element {
    if(!currentFeature){
      return;
    }

    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip');
    tooltip.dataset.feature = currentFeature.get('id');
    if (has_listeners) {
      tooltip.addEventListener('mouseover', this.hoverTooltip);
      tooltip.addEventListener('mouseout', this.mouseOutTooltip);
      tooltip.addEventListener('click', this.clickTooltip);
    }
    const layer_id = currentFeature.get('_layer_id');
    if (!layer_id) {
      return;
    }
    const layerConfig: GeojsonLayerConfig = this.mapConfig.getLayerConfig(layer_id);
    const tooltip_property = layerConfig.hover?.tooltip;

    if (!tooltip_property) {
      return;
    }
    if (!currentFeature.get(tooltip_property)) {
      return;
    }


    tooltip.innerHTML = currentFeature.get(tooltip_property);

    // The tooltip is a dom element object and doesn't have
    // custom properties. This works but should be
    // solved otherwise.
    // @todo Somehow assign the current feature to the dom element.
    tooltip.currentFeature = currentFeature;

    // Set the tooltip property on the feature for future reference.
    currentFeature.set('_tooltip', tooltip.innerHTML);
    return tooltip;
  }

  /**
   * Get the layer config belonging to a specific feature.
   * @param feature
   * @protected
   */
  protected getLayerConfig(feature: FeatureLike) {
    const layer_id = feature.get('_layer_id');
    if (!layer_id) {
      return;
    }
    const layerConfig: GeojsonLayerConfig = this.mapConfig.getLayerConfig(layer_id);
    if (!layerConfig) {
      return;
    }
    return layerConfig;
  }

  /**
   * Get the hover property of a specific feature.
   *
   * @param feature
   * @protected
   */
  public getHoverProperty(feature: FeatureLike) {
    return this.getLayerConfig(feature)?.hover.property;
  }


  /**
   * Get the hover match property of a specific feature.
   *
   * @param feature
   * @protected
   */
  public getHoverMatchProperty(feature: FeatureLike) {
    return this.getLayerConfig(feature)?.hover.property_match;
  }

  addZoomInteractions() {
    if (this.mapConfig.getView().ajax_on_view_change) {
      this.map.on('moveend', (e) => {
        ajaxUpdateMap(this);
      })
    }
  }

  /**
   * Add hover interactions to the map.
   */
  addHoverInteractions() {

    this.map.on('pointermove', (e) => {
      this.geometryHashes = [];
      if (this.map.get('drawing')) {
        return;
      }
      // Reset the cursor.
      this.map.getViewport().style.cursor = 'pointer';

      // Don't react to dragging.
      if (e.dragging) return;

      // Reset the tooltip.
      this.tooltipElement.innerHTML = '';

      const coordinate = e.coordinate;
      this.map.getOverlayById('tooltip').setPosition(coordinate);

      // Disable all hover status

      const allFeatures = this.explodeClusterFeatures(this.getAllFeatures());

      let featuresAtPixel = this.map.getFeaturesAtPixel(e.pixel);

      this.explodeClusterFeatures(featuresAtPixel);

      for (const delta in featuresAtPixel) {
        const feature = featuresAtPixel[delta];
        const tooltip =  this.getTooltip(feature);

        if(!tooltip){
          delete featuresAtPixel[delta];
        }
      }

      allFeatures.forEach((feature: Feature) => {
        const hover_property = this.getHoverProperty(feature);
        if (hover_property) {
          if (!featuresAtPixel.length) {
            feature.unset(hover_property, true);
          } else {
            feature.set(hover_property, false, true);
          }
        }
      });

      featuresAtPixel.forEach((featureAtPixel: Feature) => {

        if(featureAtPixel.get('is_intersected') === false){
            return;
        }
        const hover_property = this.getHoverProperty(featureAtPixel);
        if (hover_property) {

          // Show pointer and add feature to hovereable features array.
          this.map.getViewport().style.cursor = 'pointer';

          const hover_property_match = this.getHoverMatchProperty(featureAtPixel);

          if (featureAtPixel.get(hover_property) !== true) {
            featureAtPixel.set(hover_property, true, true);
          }
          if (hover_property_match) {
            allFeatures.forEach((feature: Feature) => {
              if (feature.get(hover_property_match) === featureAtPixel.get(hover_property_match)) {
                if (feature.get(hover_property) !== true) {
                  feature.set(hover_property, true, true);
                }
              }
            });
          }
        }
      });

      // Remove hovered features that are created when using draw tools by
      // checking if they have a hover property.

      // Only show one tooltip.
      if (featuresAtPixel.length > 0) {
        this.map.getViewport().style.cursor = 'pointer';
        const tooltip = this.getTooltip(Object.values(featuresAtPixel)[0]);
        if (tooltip !== undefined) {
          this.tooltipElement.appendChild(tooltip);
        }
      }

      // If there are more layers, add a '+X' element.
      if (Object.values(featuresAtPixel).length > 1) {
        const tooltipCountElement = document.createElement('div');
        tooltipCountElement.classList.add('tooltip-count');
        tooltipCountElement.innerHTML = '+' + (Object.values(featuresAtPixel).length - 1);
        this.tooltipElement.appendChild(tooltipCountElement);
      }


      this.triggerChange();
    });

  }

  /**
   * Execute callback on a tooltip.
   */
  clickTooltip = (e: PointerEvent): void => {
    const currentFeature = e.currentTarget?.currentFeature;
    if (!currentFeature) {
      return;
    }
    this.executeAjaxCallback(currentFeature);
    e.preventDefault();
    return;
  };

  mouseOutTooltip = (e: MouseEvent): void => {

    const currentFeature = e.currentTarget?.currentFeature;
    if (!currentFeature) {
      return;
    }

    const layer_id = currentFeature.get('_layer_id');
    const source = this.getSourceByLayerId(layer_id);
    const layerConfig = this.getLayerConfig(currentFeature);
    const hover_property = layerConfig.hover.property;
    source.getFeatures().forEach((feature: Feature) => {
      feature.set(hover_property, false, true);
    });

    this.triggerChange();
  }

  /**
   * Hover on a tooltip.
   */
  hoverTooltip = (e: MouseEvent): void => {
    this.tooltipElement.innerHTML = '';
    const currentFeature = e.currentTarget?.currentFeature;
    if (!currentFeature) {
      this.getAllFeatures().forEach((feature: Feature) => {
        feature.unset('_hovered');
      });
      return;
    }
    const layer_id = currentFeature.get('_layer_id');
    const source = this.getSourceByLayerId(layer_id);
    const layerConfig: GeojsonLayerConfig = this.mapConfig.getLayerConfig(layer_id);
    const hover_property = layerConfig.hover.property;
    const hover_property_match = layerConfig.hover.property_match;
    let extent = createEmpty();
    source.getFeatures().forEach((feature: Feature) => {
      if (currentFeature.get(hover_property_match) === feature.get(hover_property_match)) {
        feature.set(hover_property, true, true);
        extent = extend(feature.getGeometry().getExtent(), extent);
      }
    });


    this.triggerChange();
  };
}
